import { PollyClient, SynthesizeSpeechCommand } from "@aws-sdk/client-polly";

const POLLY_REGION = process.env.REACT_APP_AWS_POLLY_REGION || "us-east-1";

const createPollyClient = () => {
  return new PollyClient({
    region: POLLY_REGION,
    credentials: {
      accessKeyId: process.env.REACT_APP_AWS_POLLY_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_AWS_POLLY_SECRET_ACCESS_KEY,
    },
  });
};

const POLLY_VOICES_API = "https://grj87qmm19.execute-api.us-east-1.amazonaws.com/Test/ListPollyVoices";

let cachedVoices = null;
const getSupportedEngine = (voice, preferredEngine = "neural") => {
  if (!voice) return "standard";

  if (voice.supportedEngines.includes(preferredEngine)) {
    return preferredEngine;
  }

  // fallback to standard if neural not supported
  if (voice.supportedEngines.includes("standard")) {
    return "standard";
  }

  // fallback to first available engine
  return voice.supportedEngines[0];
};
/**
 * Fetch the list of available Polly voices from the backend API.
 * Results are cached after the first successful call.
 *
 * @returns {Promise<Array<{id: string, name: string, language: string, engine: string}>>}
 */
export const fetchPollyVoices = async () => {
  if (cachedVoices) return cachedVoices;

  try {
    const res = await fetch(POLLY_VOICES_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    const data = await res.json();
    const parsedBody = typeof data.body === "string" ? JSON.parse(data.body) : data;
    const voices = parsedBody?.Voices || [];

    cachedVoices = voices.map((v) => ({
      id: v.Id,
      name: v.Name || v.Id,
      language: v.LanguageName || "Unknown",
      supportedEngines: v.SupportedEngines || ["standard"],
      // engine: v.SupportedEngines?.includes("neural") ? "neural" : "standard",
    }));

    return cachedVoices;
  } catch (err) {
    console.error("Error fetching Polly voices:", err);
    return [];
  }
};

/**
 * Synthesize speech using AWS Polly and return a base64-encoded WAV string.
 *
 * @param {string} text      The text to convert to speech.
 * @param {string} voiceId   The Polly Voice ID (e.g. "Joanna").
 * @param {string} engine    "neural" or "standard" (default: "neural").
 * @returns {Promise<string>} Base64-encoded audio (pcm → wav).
 */
export const synthesizeSpeech = async (
  text,
  voiceId = "Joanna",
  preferredEngine = "neural"
) => {
  const client = createPollyClient();

  const trySynthesize = async (engine) => {
    const command = new SynthesizeSpeechCommand({
      Text: text,
      OutputFormat: "pcm",
      VoiceId: voiceId,
      Engine: engine,
      SampleRate: "8000",
    });

    const response = await client.send(command);
    const arrayBuffer = await streamToArrayBuffer(response.AudioStream);
    return pcmToWav(arrayBuffer, 8000, 1, 16);
  };

  try {
    // 🔹 First try preferred engine (neural)
    const wavBuffer = await trySynthesize(preferredEngine);
    console.log(`Using ${preferredEngine} engine`);
    return arrayBufferToBase64(wavBuffer);

  } catch (err) {
    console.warn(`${preferredEngine} failed, retrying with standard...`, err);

    try {
      // 🔹 Fallback to standard
      const wavBuffer = await trySynthesize("standard");
      // console.log(`Fallback to standard engine`);
      return arrayBufferToBase64(wavBuffer);

    } catch (err2) {
      console.error("Both engines failed:", err2);
      throw err2;
    }
  }
};

/**
 * Convert a ReadableStream (or Uint8Array) from Polly into an ArrayBuffer.
 */
const streamToArrayBuffer = async (stream) => {
  if (stream instanceof Uint8Array) {
    return stream.buffer;
  }
  // Browser ReadableStream
  const reader = stream.getReader();
  const chunks = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result.buffer;
};

/**
 * Wrap raw 16-bit PCM data in a WAV (RIFF) container.
 */
const pcmToWav = (pcmBuffer, sampleRate, numChannels, bitsPerSample) => {
  const pcmBytes = new Uint8Array(pcmBuffer);
  const dataLength = pcmBytes.length;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);                                    // PCM sub-chunk size
  view.setUint16(20, 1, true);                                     // Audio format: PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
  view.setUint16(32, numChannels * (bitsPerSample / 8), true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, "data");
  view.setUint32(40, dataLength, true);

  // Copy PCM data
  new Uint8Array(buffer, 44).set(pcmBytes);

  return buffer;
};

/**
 * Convert an ArrayBuffer to a Base64 string.
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};
